import {inject, Injectable} from '@angular/core';
import {BehaviorSubject, distinctUntilChanged, ReplaySubject, Subject} from 'rxjs';
import {PivotCell} from '../../models/pivot-table/pivot-cell';
import {GridCoordinates} from '../../models/pivot-table/grid-coordinates';
import {GridNavigationEvent} from '../../models/pivot-table/grid-navigation-event';
import {ParamMap} from '@angular/router';
import {parseInteger} from '../../services/functions/parse-integer';
import {PagingOptionalRouteParams} from '../../models/route-params/paging-optional-route-params';
import {LoggerService} from '../../services/logger.service';
import {shareReplay} from 'rxjs/operators';
import {MCAG} from '../../services/api/fit-api/models/mcag';
import {SortedBy} from 'src/app/models/sorted-by';
import {selectedSortedByParser} from '../../services/functions/selected-sorted-by-string-parser';
import {SortedByOptionalRouteParams} from '../../models/route-params/sorted-by-optional-route-param';

export enum ContentDirection {
	// All content is shown
	none,
	// Content is hidden to the left
	left,
	// Content is hidden to the right
	right,
	// Content is hidden to both sides
	both
}

@Injectable({
	providedIn: 'root'
})
export class DataGridService {
	private logger = inject(LoggerService);

	// The number of cells to move for `PageDown`/`PageUp` keys
	keyboardPageSize = 10;

	// When grid pagination is in use, store the start and end body row indexes that are visible
	public bodyRowDisplayStart?: number;
	public bodyRowDisplayEnd?: number;

	private _hasOverflowContent = new Subject<ContentDirection>();
	// Indicates in which direction the grid container can be scrolled
	hasOverflowContent = this._hasOverflowContent.asObservable();

	private _frozenColumnWidth = new Subject<number>();
	// keeps track of the rendered width of the frozen column in order to shrink OS
	frozenColumnWidth = this._frozenColumnWidth.asObservable();

	private _columnIndexInView = new Subject<number | null>();
	// The single column that is in view on two-column (mobile) layout
	// todo should this just be column indexes in view? Then the shadow might
	//  be easier
	columnIndexInView = this._columnIndexInView.asObservable().pipe(
		distinctUntilChanged()
	);

	//	BehaviorSubject to replay last value to late subscribers
	private _focusedCell = new BehaviorSubject<GridCoordinates>(new GridCoordinates(0, 0));
	// The coordinates of the cell that currently has focus (also used to set tabindexes)
	focusedCell = this._focusedCell.asObservable();

	// todo create observable registration
	setHasOverflowContent(value: ContentDirection) {
		this._hasOverflowContent.next(value);
	}

	setColumnIndexInView(value: number | null) {
		// this.logger.debug('setColumnIndexInView', value);
		this._columnIndexInView.next(value);
	}

	setFrozenColumnWidth(value: number) {
		this._frozenColumnWidth.next(value);
	}

	// handles keyboard events while cell is focused in order to focus the next cell
	gridCellKeyboardEvent<T extends object>(event: KeyboardEvent, cell: PivotCell<T>) {
		const gridNavigationEvent = GridNavigationEvent.fromKeyboardEvent(event, this.keyboardPageSize);
		if (gridNavigationEvent) {
			// disable keyboard scroll if handling a GridNavigationEvent
			event.preventDefault();

			const nextCell =
				cell.getCellCoordinates(gridNavigationEvent.direction, gridNavigationEvent.offset, this.bodyRowDisplayStart, this.bodyRowDisplayEnd);

			// Only focus if there is a next cell in the specified direction (i.e., not the end of the grid)
			if (nextCell) {
				this._focusedCell.next(nextCell);
			}
		}
	}

	// allowed page sizes; first value is treated as default
	paginationSizeOptions: Array<number> = paginationSizeOptions;
	static defaultPageNumber = 1;
	pageNumber = new ReplaySubject<number>(1);  // Current page number
	distinctPageNumber = this.pageNumber.pipe(distinctUntilChanged(), shareReplay(1));
	pageSize = new ReplaySubject<number>(1); // Current page size
	distinctPageSize = this.pageSize.pipe(distinctUntilChanged(), shareReplay(1));

	userRequestsChangeToPageNumber = new ReplaySubject<number>(1);
	userRequestsChangeToPageSize = new ReplaySubject<number>(1);
	userRequestsChangeToSortedBy = new ReplaySubject<string>(1);


	selectedCellRowIndex = new ReplaySubject<number | undefined>(1);
	selectedCellColumnIndex = new ReplaySubject<number | undefined>(1);

	horizontalScrollTo = new ReplaySubject<number>();

	/**
	 * Sorted By option for the Dollars / By Governments page.
	 *
	 * todo possibly handle where value isn't in applied year range?
	 */
	static processSortedBy(routeParams: ParamMap, savedParams: SortedByOptionalRouteParams, defaultSortedBy: SortedBy) {
		// Clear out any existing values in our saved optional params
		delete savedParams.sortedBy;

		// Check for values in route parameters
		const sortedBy = routeParams.get('sortedBy');

		// Set applied values (use defaults if optional route parameters not provided)
		let appliedSortedBy: SortedBy | undefined = defaultSortedBy; // default

		if (sortedBy) {
			appliedSortedBy = selectedSortedByParser(sortedBy);
			// Check if the parsing result was able to be parsed
			if (appliedSortedBy === undefined) {
				throw new Error(`Invalid sorted by: ${appliedSortedBy} is not a valid sorted by value.`);
			} else {
				savedParams.sortedBy = appliedSortedBy.toString();
			}
		}

		return appliedSortedBy;
	}

	/**
	 * Inspect route parameters for paging state parameters and set the applied values using the provided values or defaults
	 *
	 * @param routeParams - raw params from the route
	 * @param savedParams - saved params after parsing (used for badge counts and reconstructing the parameters during route navigation)
	 */
	static processPagingRouteParams(routeParams: ParamMap, savedParams: PagingOptionalRouteParams ) {
		// Clear out any existing values in our saved optional params
		delete savedParams.page;
		delete savedParams.pageSize;

		// Check for values in route parameters
		const pageNumber = routeParams.get('page');
		const pageSize = routeParams.get('pageSize');

		// Set applied values (use defaults if optional route parameters not provided)
		let appliedPageNumber = DataGridService.defaultPageNumber; // default page
		let appliedPageSize = paginationSizeOptions[0]; // default page size
		if (pageNumber) {
			appliedPageNumber = parseInteger(pageNumber, 'route parameter page');
			savedParams.page = appliedPageNumber;
		}
		if (pageSize) {
			appliedPageSize = parseInteger(pageSize, 'route parameter pageSize');
			// if page size isn't in options list revert to default
			if (!paginationSizeOptions.find(p => p === appliedPageSize)) {
				appliedPageSize = paginationSizeOptions[0];
			}
			savedParams.pageSize = appliedPageSize;
		}

		return [appliedPageNumber, appliedPageSize];
	}

	/**
	 * Return page to scroll to based on a list of mcags (sorted), the size of each page, and the mcag to scroll to
	 *
	 * @param mcags
	 * @param pageSize
	 * @param mcag
	 */
	static getPageNumber(mcags: MCAG[], pageSize: number, mcag: MCAG) {
		const foundIndex = mcags.findIndex(m => m === mcag);
		const newPageNumber = Math.floor(foundIndex / pageSize) + 1;
		return newPageNumber;
	}
}

export const paginationSizeOptions: Array<number> = [10, 20, 40];
